import React, { useEffect, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, ArrowDownIcon, ChevronRightIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { trackEvent as track } from '../utils/analytics';
const reveal = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7
  }
};
const OUTCOMES = [{
  stat: '89%',
  label: 'Leak Reduction Potential'
}, {
  stat: '34%',
  label: 'Faster Repair Response'
}, {
  stat: '5–15%',
  label: 'Consumption Reduction'
}];
const ENVIRONMENT = ['Smart Water Deployment', 'Verified Identity Infrastructure', 'Leak Detection Workflows', 'Law 25 Ready', 'Canadian Hosted', 'Auditable Actions'];
const OPERATIONAL_FLOW = ['Verified Owner', 'Property', 'Water Meter', 'Consumption Data', 'ATNA Detection', 'Owner Notification', 'Request Creation', 'Resolution'];
export function Casselman() {
  useEffect(() => {
    track('Case Study Viewed', {
      page: 'Town of Casselman'
    });
  }, []);
  return <div className="w-full min-h-screen bg-white" data-id="element-1288">
      <Header data-id="element-1289" />

      {/* Breadcrumb bar */}
      <div className="fixed top-[65px] left-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-b border-[#E4E6EC]" data-id="element-1290">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between" data-id="element-1291">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 font-sans text-sm" data-id="element-1292">
            <Link to="/" className="text-[#121212]/50 hover:text-[#121212] transition-colors" data-id="element-1293">
              Home
            </Link>
            <ChevronRightIcon className="w-3.5 h-3.5 text-[#121212]/30" data-id="element-1294" />
            <Link to="/smart-water" className="text-[#121212]/50 hover:text-[#121212] transition-colors" data-id="element-1295">
              Smart Water
            </Link>
            <ChevronRightIcon className="w-3.5 h-3.5 text-[#121212]/30" data-id="element-1296" />
            <span className="text-[#121212] font-medium" data-id="element-1297">
              Town of Casselman
            </span>
          </nav>
          <Link to="/smart-water" className="font-sans text-sm text-[#121212]/50 hover:text-[#121212] transition-colors" data-id="element-1298">
            Back to Smart Water
          </Link>
        </div>
      </div>

      <main className="pt-[120px]" data-id="element-1299">
        {/* HERO */}
        <section className="pt-24 pb-24 px-6" data-id="element-1300">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1301">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="font-sans text-xs font-semibold tracking-[0.25em] text-indigo uppercase mb-6" data-id="element-1302">
              Case Study · Live Production Environment
            </motion.div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.1
          }} className="font-serif text-5xl md:text-6xl text-[#121212] mb-6" data-id="element-1303">
              Town of Casselman
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="font-sans text-xl text-[#121212]/60" data-id="element-1304">
              Smart Water in production.
            </motion.p>
          </div>
        </section>

        {/* RESEARCH-BACKED OUTCOMES */}
        <section className="py-28 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-1305">
          <div className="max-w-5xl mx-auto" data-id="element-1306">
            <motion.h2 {...reveal} className="font-serif text-3xl md:text-4xl text-[#121212] text-center mb-16" data-id="element-1307">
              Research-backed outcomes.
            </motion.h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E4E6EC] border border-[#E4E6EC] mb-6" data-id="element-1308">
              {OUTCOMES.map(card => <div key={card.label} className="bg-white p-10 text-center" data-id="element-1309">
                  <div className="font-serif text-5xl text-[#121212] mb-3" data-id="element-1310">
                    {card.stat}
                  </div>
                  <div className="font-sans text-sm font-semibold tracking-wide text-indigo uppercase" data-id="element-1311">
                    {card.label}
                  </div>
                </div>)}
            </div>
            <p className="font-sans text-xs text-[#121212]/45 leading-relaxed max-w-3xl mx-auto text-center" data-id="element-1312">
              *Research findings from peer-reviewed smart water and consumption
              feedback studies. Results vary by deployment, infrastructure and
              user participation.
            </p>
          </div>
        </section>

        {/* LIVE PRODUCTION ENVIRONMENT */}
        <section className="py-28 px-6 border-t border-[#E4E6EC]" data-id="element-1313">
          <div className="max-w-4xl mx-auto" data-id="element-1314">
            <motion.h2 {...reveal} className="font-serif text-3xl md:text-4xl text-[#121212] text-center mb-16" data-id="element-1315">
              Live production environment.
            </motion.h2>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="grid grid-cols-1 sm:grid-cols-2 gap-y-6 gap-x-12 max-w-2xl mx-auto" data-id="element-1316">
              {ENVIRONMENT.map(item => <div key={item} className="flex items-center gap-3" data-id="element-1317">
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo shrink-0" data-id="element-1318" />
                  <span className="font-sans text-base text-[#121212]" data-id="element-1319">
                    {item}
                  </span>
                </div>)}
            </motion.div>
          </div>
        </section>

        {/* OPERATIONAL FLOW */}
        <section className="py-28 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-1320">
          <div className="max-w-3xl mx-auto" data-id="element-1321">
            <motion.h2 {...reveal} className="font-serif text-3xl md:text-4xl text-[#121212] text-center mb-16" data-id="element-1322">
              Operational flow.
            </motion.h2>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="flex flex-col items-center" data-id="element-1323">
              {OPERATIONAL_FLOW.map((node, i, arr) => <Fragment key={node} data-id="element-1324">
                  <div className={`w-full max-w-md py-4 px-6 text-center border font-sans text-sm ${node === 'ATNA Detection' ? 'border-indigo bg-indigo/5 text-indigo font-medium' : 'border-[#E4E6EC] bg-white text-[#121212]'}`} data-id="element-1325">
                    {node}
                  </div>
                  {i < arr.length - 1 && <ArrowDownIcon className="w-4 h-4 text-[#E4E6EC] my-2" data-id="element-1326" />}
                </Fragment>)}
            </motion.div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-1327">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1328">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] mb-10" data-id="element-1329">
              Ready to discuss your deployment?
            </motion.h2>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} data-id="element-1330">
              <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
              location: 'Casselman Final'
            })} className="inline-flex items-center gap-2 px-8 py-4 bg-indigo text-white text-base font-medium rounded-lg hover:bg-indigo/90 transition-colors" data-id="element-1331">
                Request a Demo
                <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-1332" />
              </Link>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer data-id="element-1333" />
    </div>;
}